import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'

const DISMISS_KEY = 'ratygo:embed-popup-dismissed'
const SHOW_DELAY_MS = 8_000

/**
 * Bottom-right popup nudging the small subset of homepage visitors who are
 * bloggers/doradcy toward /widget-hipoteczny. Most visitors are people
 * calculating their own mortgage — don't interrupt them.
 *
 * - 8s delay so the user engages with the calculator first
 * - Desktop-only (lg:block, ≥1024px) — bloggers embed from desktop; a corner
 *   popup on tablets and phones would block calculator controls
 * - One-shot: once dismissed (X or CTA click), never shown again for this
 *   browser. localStorage key includes a namespace prefix in case other
 *   future popups want their own state.
 * - ESC to close (keyboard accessibility)
 * - Brief fade-in via CSS animation (no JS animation lib needed — that combo
 *   was producing intermittent mount/unmount issues in dev)
 */
export function EmbedPopup() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (localStorage.getItem(DISMISS_KEY) === 'true') return
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS)
    return () => clearTimeout(timer)
  }, [])

  const handleDismiss = useCallback(() => {
    setVisible(false)
    localStorage.setItem(DISMISS_KEY, 'true')
  }, [])

  useEffect(() => {
    if (!visible) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleDismiss()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [visible, handleDismiss])

  if (!visible) return null

  return (
    <aside
      role="region"
      aria-label="Propozycja dla blogerów"
      className="hidden lg:block fixed bottom-6 right-6 z-50 w-[320px] rounded-2xl bg-white shadow-xl border border-indigo-100 p-5 embed-popup-enter"
    >
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Zamknij"
        className="absolute top-2 right-2 w-7 h-7 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <div className="flex items-start gap-3 pr-6">
        <span
          className="shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-[#6366f1] to-[#8b5cf6] flex items-center justify-center text-white"
          aria-hidden="true"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
          </svg>
        </span>
        <div className="min-w-0">
          <p className="text-sm font-bold text-foreground leading-tight">
            Prowadzisz bloga lub doradzasz?
          </p>
          <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
            Wstaw ten kalkulator na swoją stronę — bezpłatnie, w 30 sekund.
          </p>
          <Link
            to="/widget-hipoteczny"
            onClick={handleDismiss}
            className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-primary hover:underline"
          >
            Zobacz jak →
          </Link>
        </div>
      </div>
    </aside>
  )
}
